import SEOHead from "./components/SEOHead";
import UsagePresence from "./components/UsagePresence";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { ThemeProvider } from "./components/ThemeProvider";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";

import Dashboard from "./pages/Dashboard";
import Opportunities from "./pages/Opportunities";
import AlertSettings from "./pages/AlertSettings";
import Watchlist from "./pages/Watchlist";
import Scanner from "./pages/Scanner";
import Comparateur from "./pages/Comparateur";
import Products500 from "./pages/Products500";
import Fournisseurs300 from "./pages/Fournisseurs300";
import MarketplaceProducts from "./pages/MarketplaceProducts";
import CustomsCalculator from "./pages/CustomsCalculator";
import SpinWheel from "./pages/SpinWheel";
import Affiliation from "./pages/Affiliation";
import AdminDashboard from "./pages/AdminDashboard";
import Subscription from "./pages/Subscription";
import LandingPage from "./pages/LandingPage";
import Blog from "./pages/Blog";
import Contact from "./pages/Contact";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import Layout from "./components/Layout";
import ProfimySuite from "./components/ProfimySuite";
import SubscriptionGate from "./components/SubscriptionGate";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#00E676]" size={32} />
      </div>
    );
  }
  
  if (!user || user === false) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Detect session_id during render (NOT in useEffect)
  // This synchronous check prevents race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
    <SEOHead />
    <UsagePresence />
    <Routes>
      {/* Public pages */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/outils" element={<Navigate to="/outils/profimy-app" replace />} />
      <Route path="/outils/:tool" element={<ProfimySuite current="profimy-app" home="/dashboard" />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<Blog />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected app pages - with sidebar */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="opportunities" element={<Opportunities />} />
        <Route path="scanner" element={<SubscriptionGate><Scanner /></SubscriptionGate>} />
        <Route path="comparateur" element={<SubscriptionGate><Comparateur /></SubscriptionGate>} />
        <Route path="watchlist" element={<SubscriptionGate><Watchlist /></SubscriptionGate>} />
        <Route path="produits-500" element={<Products500 />} />
        <Route path="fournisseurs-300" element={<Fournisseurs300 />} />
        <Route path="produits-marketplaces" element={<MarketplaceProducts />} />
        <Route path="calculateur-douane" element={<SubscriptionGate><CustomsCalculator /></SubscriptionGate>} />
        <Route path="spin" element={<SpinWheel />} />
        <Route path="notifications" element={<SubscriptionGate><AlertSettings /></SubscriptionGate>} />
        <Route path="subscription" element={<Subscription />} />
        <Route path="affiliation" element={<Affiliation />} />
        <Route path="contact" element={<Contact />} />
        <Route path="admin" element={<AdminDashboard />} />
      </Route>
    </Routes>
    </>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="profimy-theme">
      <ErrorBoundary>
      <AuthProvider>
        <SubscriptionProvider>
          <div className="App">
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
            <Toaster position="top-right" />
          </div>
        </SubscriptionProvider>
      </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
